import { useMemo, useState } from "react";

import Input from "../../controls/Input";
import { config } from "../../../constants";
import { resolveTenantAssetUrl } from "../../../shared/tenantBranding";
import { LABEL_OPTIONS } from "../../../shared/tenantSettingsForm";
import {
  isValidHexColor,
  isValidSlug,
  validateLogoFile,
} from "../../../shared/tenantValidation";
import Button from "../../controls/Button";

const fullWidthInputClass = "input-brand";

function SettingsCard({ title, description, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-border-subtle bg-card shadow-soft">
      <div className="border-b border-border-subtle px-6 py-5">
        <h2 className="text-base font-semibold text-brand">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        ) : null}
      </div>
      <div className="space-y-5 p-6">{children}</div>
    </section>
  );
}

export default function CompanySettingsForm({
  formValues,
  logoPreview,
  logoFile,
  saving,
  uploadingLogo,
  updateField,
  resetChanges,
  handleLogoSelect,
  uploadLogo,
  saveSettings,
}) {
  const [errors, setErrors] = useState({});

  const displayedLogo = useMemo(() => {
    if (logoPreview) {
      return logoPreview;
    }
    return resolveTenantAssetUrl(
      formValues.logo_url,
      config.url.STORAGE_URL,
    );
  }, [formValues.logo_url, logoPreview]);

  const validate = () => {
    const nextErrors = {};

    if (!formValues.name?.trim()) {
      nextErrors.name = "Company name is required.";
    } else if (formValues.name.length > 255) {
      nextErrors.name = "Company name must be 255 characters or fewer.";
    }

    if (!formValues.slug?.trim()) {
      nextErrors.slug = "Company slug is required.";
    } else if (!isValidSlug(formValues.slug)) {
      nextErrors.slug =
        "Slug must be lowercase letters, numbers, and hyphens only.";
    }

    if (!isValidHexColor(formValues.primary_color)) {
      nextErrors.primary_color = "Enter a valid hex color (e.g. #0B1F3A).";
    }

    if (logoFile) {
      const logoError = validateLogoFile(logoFile);
      if (logoError) {
        nextErrors.logo = logoError;
      }
    }

    LABEL_OPTIONS.forEach(({ key }) => {
      const value = formValues.labels?.[key];
      if (value && value.length > 100) {
        nextErrors[`label_${key}`] = "Label must be 100 characters or fewer.";
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      saveSettings(formValues);
    }
  };

  const handleLogoInput = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const logoError = validateLogoFile(file);
    if (logoError) {
      setErrors((current) => ({ ...current, logo: logoError }));
      return;
    }

    setErrors((current) => ({ ...current, logo: null }));
    handleLogoSelect(file);
    uploadLogo(file);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <SettingsCard
        title="Company Profile"
        description="Basic company information shown across the platform."
      >
        <Input
          label="Company Name"
          name="name"
          onChange={(e) => updateField("name", e.target.value)}
          value={formValues.name}
          labelOn={true}
          type="text"
          error={errors.name}
        />
        <Input
          label="Company Email"
          name="company_email"
          onChange={(e) => updateField("company_email", e.target.value)}
          value={formValues.company_email}
          labelOn={true}
          type="email"
        />
        <Input
          label="Phone Number"
          name="phone_number"
          onChange={(e) => updateField("phone_number", e.target.value)}
          value={formValues.phone_number}
          labelOn={true}
          type="text"
        />
        <div className="rounded-[14px] border border-[#E2E8F0] bg-[#F8FAFC] p-5">
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#0F172A]">Address</p>
            <p className="mt-0.5 text-xs text-[#64748B]">
              Company mailing address for documents and communications.
            </p>
          </div>
          <div className="space-y-4">
            <Input
              label="Address Line 1"
              name="address1"
              onChange={(e) => updateField("address1", e.target.value)}
              value={formValues.address1}
              labelOn={true}
              type="text"
              placeholder="Street address, P.O. box"
              className={fullWidthInputClass}
            />
            <Input
              label="Address Line 2"
              name="address2"
              onChange={(e) => updateField("address2", e.target.value)}
              value={formValues.address2}
              labelOn={true}
              type="text"
              placeholder="Apartment, suite, unit (optional)"
              className={fullWidthInputClass}
            />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Input
                label="City"
                name="city"
                onChange={(e) => updateField("city", e.target.value)}
                value={formValues.city}
                labelOn={true}
                type="text"
                className={fullWidthInputClass}
              />
              <Input
                label="State / Province"
                name="state"
                onChange={(e) => updateField("state", e.target.value)}
                value={formValues.state}
                labelOn={true}
                type="text"
                className={fullWidthInputClass}
              />
              <Input
                label="Postal Code"
                name="postal_code"
                onChange={(e) => updateField("postal_code", e.target.value)}
                value={formValues.postal_code}
                labelOn={true}
                type="text"
                className={fullWidthInputClass}
              />
            </div>
            <Input
              label="Country"
              name="country"
              onChange={(e) => updateField("country", e.target.value)}
              value={formValues.country}
              labelOn={true}
              type="text"
              className={fullWidthInputClass}
            />
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Branding" description="Add your logo.">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-[#0F172A]">
                Logo
              </label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={handleLogoInput}
                disabled={uploadingLogo}
                className="block w-full text-sm text-[#64748B] file:mr-4 file:rounded-lg file:border-0 file:bg-[#F1F5F9] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[#0F172A] hover:file:bg-[#E2E8F0] disabled:opacity-50"
              />
              {errors.logo ? (
                <p className="mt-1 text-xs font-medium text-[#DC2626]">
                  {errors.logo}
                </p>
              ) : (
                <p className="mt-1 text-xs text-[#64748B]">
                  PNG, JPG, SVG, or WebP. Max 2MB.
                </p>
              )}
              {uploadingLogo ? (
                <p className="mt-3 text-xs font-medium text-[#0F172A]">
                  Uploading logo...
                </p>
              ) : null}
            </div>
          </div>

          <div className="border-t border-[#E2E8F0] pt-6 text-center lg:min-h-full lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#64748B]">
              Brand Preview
            </p>
            <p className="mt-3 text-sm font-semibold text-[#0F172A]">
              {formValues.name || "Company Name"}
            </p>
            <div className="mt-5 flex min-h-[140px] items-center justify-center rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-5 py-6">
              {displayedLogo ? (
                <img
                  src={displayedLogo}
                  alt="Company logo preview"
                  className="max-h-24 w-auto max-w-full object-contain drop-shadow-sm"
                />
              ) : (
                <span className="text-sm text-[#64748B]">
                  No logo selected
                </span>
              )}
            </div>
            {uploadingLogo ? (
              <p className="mt-3 text-xs font-medium text-[#0F172A]">
                Uploading new logo...
              </p>
            ) : null}
          </div>
        </div>
      </SettingsCard>

      <div className="flex flex-wrap justify-end gap-3 rounded-[18px] border border-[#E2E8F0] bg-white px-6 py-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <Button
          type="button"
          variant="secondary"
          className="h-10 px-4"
          onClick={resetChanges}
          disabled={saving}
        >
          Reset Changes
        </Button>
        <Button type="submit" className="h-10 px-4" loading={saving}>
          Save Changes
        </Button>
      </div>
    </form>
  );
}
